/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { IncomeSourceModel } from 'src/types/income-source.model';
import { MatchModel } from 'src/types/match.model';
import FormatService from '../../services/format.service';
import MatchService from '../../services/match.service';
import UtilsService from '../../services/utils.service';

// components
import Dropdown from '../../components/menus/Dropdown';
import { ButtonPrimary, ButtonSecondary, IconInfo, Input, Label } from '@americanexpress/dls-react';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and styles
import './MatchDrawer.scss';

export default class MatchDrawer extends Component<
    any,
    {
        match: MatchModel;
    }
> {
    public override state = {
        match: null as MatchModel
    };

    public static getDerivedStateFromProps(props: any, prevState: any): any {
        const match: MatchModel = prevState.match || props.drawerProps.match;
        if (!match.incomeUid) {
            match.incomeUid = _.get(props, 'drawerProps.incomeSources[0].incomeUid');
            match.preTaxSalary = _.get(match, 'source.annualizedIncome');
            if (match.preTaxSalary) match.preTaxSalary = match.preTaxSalary * 1.25;
        }
        return { match: prevState.match || props.drawerProps.match };
    }

    public override render(): ReactComponentElement<any> {
        return (
            <div className="match-drawer drawer">
                <Typography variant={TypographyVariants.HEADING_S} className="page-title">
                    Employer Matching Contributions
                </Typography>
                <Typography variant={TypographyVariants.CAPTION} className="page-description after24">
                    Employer matching funds are risk-free returns on your investments. Let's make sure you are getting
                    the most you can.
                </Typography>

                <Label htmlFor="income-source" className="std-label">
                    Which income has the match?
                </Label>
                <Dropdown
                    value={_.get(this.state, 'match.incomeUid')}
                    onChange={(e) => {
                        this.handleChange(e, 'incomeUid');
                    }}
                    fullWidth={true}
                >
                    {this.props.drawerProps.incomeSources.map((incomeSource: IncomeSourceModel, index: number) => {
                        const name = incomeSource.name;
                        return (
                            <option key={`is-${index}`} value={incomeSource.incomeUid}>
                                {UtilsService.capitalizeFirstLetter(name)}
                            </option>
                        );
                    })}
                </Dropdown>

                <Label htmlFor="plan-type" className="std-label">
                    What type of plan do you have?
                </Label>
                <Dropdown
                    value={this.state.match.planType}
                    onChange={(e) => {
                        this.handleChange(e, 'planType');
                    }}
                    fullWidth={true}
                >
                    <option value="401k">401k</option>
                    <option value="IRA">IRA</option>
                    <option value="Other">Other</option>
                </Dropdown>

                <Typography variant={TypographyVariants.CAPTION} className="info-area" key="info-max">
                    <IconInfo className="info-icon" />
                    <span className="inner-info">The maximum contribution to a 401k in 2020 is $19,500</span>
                </Typography>

                <Label htmlFor="pre-tax-salary" className="std-label no-bottom-margin">
                    What is your pre-tax salary?
                </Label>
                <Input
                    id="pre-tax-salary"
                    key="keytax"
                    placeholder="Add Salary"
                    fullWidth
                    type="string"
                    className="after24"
                    value={FormatService.formatString(this.state.match.preTaxSalary)}
                    variant="outlined"
                    onChange={(e: any) => {
                        this.handleChange(e, 'preTaxSalary');
                    }}
                />

                <Label key="how-label" htmlFor="match-type" className="std-label">
                    How does your employer match?
                </Label>
                <Dropdown
                    value={this.state.match.matchType}
                    onChange={(e) => {
                        this.handleChange(e, 'matchType');
                    }}
                    fullWidth={true}
                >
                    <option value={0}>50%, up to 6% of salary</option>
                    <option value={1}>100%, up to 3% of salary</option>
                </Dropdown>

                <Typography variant={TypographyVariants.CAPTION} className="info-area" key="info-notshown">
                    <IconInfo className="info-icon" />
                    <span className="inner-info">
                        If your match type is not shown, choose the closest match or skip this step for now. More match
                        types coming soon.
                    </span>
                </Typography>

                <div key="match-cta" className="match-cta after24">
                    <Typography key="entire-match" variant={TypographyVariants.CAPTION} className="page-description">
                        To get your entire match, you need to contribute:
                    </Typography>
                    <Typography key="monthly" variant={TypographyVariants.HEADING_S} className="page-title">
                        {this.getAnnualContribution()}/y. or {this.getMonthlyContribution()}/mo.
                    </Typography>
                    <Typography key="match-percent" variant={TypographyVariants.HEADING_XS} className="page-title">
                        {this.getMatchPercentage()} of your salary
                    </Typography>
                </div>

                <div className="match-button-bar">
                    <ButtonSecondary
                        className="right"
                        id="skip"
                        onClick={async () => {
                            await this.props.markAchievementDone(9);
                            await MatchService.deleteMatch(this.state.match);
                            this.props.handleTopDrawer();
                            this.props.handleReloadUser(); // don't wait
                        }}
                        key="skip"
                    >
                        Skip this step
                    </ButtonSecondary>
                    <ButtonPrimary
                        className="right"
                        id="save"
                        onClick={async () => {
                            await this.handleSave();
                        }}
                        key="save"
                    >
                        Save
                    </ButtonPrimary>
                </div>
            </div>
        );
    }

    private getMatchPercentage(): string {
        switch (Number(this.state.match.matchType)) {
            case 0:
                return '6%';
            case 1:
                return '3%';
            case 2:
                return 'N/A ';
        }
    }

    private getAnnualContribution(): string {
        switch (Number(this.state.match.matchType)) {
            case 0:
                return FormatService.formatCurrency(this.state.match.preTaxSalary * 0.06);
            case 1:
                return FormatService.formatCurrency(this.state.match.preTaxSalary * 0.03);
            case 2:
                return 'N/A ';
        }
    }

    private getMonthlyContribution(): string {
        switch (Number(this.state.match.matchType)) {
            case 0:
                return FormatService.formatCurrency((this.state.match.preTaxSalary * 0.06) / 12);
            case 1:
                return FormatService.formatCurrency((this.state.match.preTaxSalary * 0.03) / 12);
            case 2:
                return 'N/A ';
        }
    }

    private async handleSave(): Promise<void> {
        const response: any = await MatchService.upsert(this.state.match);
        if (_.isObjectLike(response)) {
            // if response is not an error string
            this.props.markAchievementDone(9);
            this.props.handleTopDrawer();
            this.props.drawerProps.handleRefresh();
        }
    }

    private handleChange(event: any, name: string): void {
        const match: any = _.clone(this.state.match);

        if (name === 'incomeUid') {
            match.incomeUid = event.target.value;
            const income: IncomeSourceModel = _.find(this.props.drawerProps.incomeSources, {
                incomeUid: match.incomeUid
            });
            if (income) {
                match.preTaxSalary = income.annualizedIncome;
                if (match.preTaxSalary) match.preTaxSalary = match.preTaxSalary * 1.25;
            }
        } else if (name === 'preTaxSalary') {
            match[name] = Number(event.target.value.replace(/[^0-9.]+/g, '')).toFixed(0);
        } else {
            match[name] = event.target.value;
        }

        this.setState({
            match
        });
    }
}
